package com.projectas.app.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.projectas.app.style.TextSelectionStyle
import com.projectas.app.util.BackgroundGradient
import com.projectas.app.util.Constants.ACME_REGULAR
import com.projectas.app.util.Constants.LATO_LIGHT
import com.projectas.app.util.Constants.LATO_REGULAR
import com.projectas.app.util.Constants.SUPPORT_EMAIL
import com.projectas.app.util.TextGradient
import com.projectas.app.util.Theme
import com.varabyte.kobweb.compose.css.BackgroundClip
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@OptIn(DelicateApi::class)
@Composable
@Page
fun TermsOfServicePage() {
    val breakpoint = rememberBreakpoint()
    val largeBreakpoint = remember(breakpoint) {
        breakpoint > Breakpoint.LG
    }

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundImage(BackgroundGradient)
                .padding(
                    leftRight = if (breakpoint > Breakpoint.LG) 120.px
                    else if (breakpoint == Breakpoint.LG) 100.px
                    else if (breakpoint > Breakpoint.MD) 70.px
                    else if (breakpoint == Breakpoint.MD) 50.px
                    else 40.px
                ),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(if (largeBreakpoint) 800.px else 380.px)
                    .padding(bottom = 100.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Main Title
                H1(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 20.px, top = if (largeBreakpoint) 50.px else 20.px)
                        .backgroundImage(TextGradient)
                        .background { this.clip(BackgroundClip.Text) }
                        .color(Color.transparent)
                        .fontFamily(ACME_REGULAR)
                        .fontSize(if (largeBreakpoint) 4.cssRem else 3.cssRem)
                        .lineHeight(1.2)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Terms of Service")
                }

                // Subtitle Information
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.TextLowVisibility.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.4)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Effective Date: 26.10.2025")
                }

                Surface(modifier = Modifier.height(20.px)) {}

                // Introduction
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text("Welcome to The Projectas! These Terms of Service (\"Terms\") govern your use of The Projectas mobile application (\"App\"), operated by STIV Apps (\"we,\" \"our,\" or \"us\"). By downloading, accessing, or using The Projectas, you agree to these Terms. If you do not agree, please do not use the App.")
                }

                // Section 1
                SectionTitle("1. Description of the Service", largeBreakpoint)
                SectionContent(
                    "The Projectas is a productivity application designed to help users manage projects, tasks, versions, time tracking, analytics, deadlines, and notifications.",
                )
                SectionContent(
                    "The service is provided for organizational and productivity purposes only.",
                )

                // Section 2
                SectionTitle("2. User Accounts", largeBreakpoint)
                SectionContent(
                    "To use The Projectas, you must sign in using a supported third-party authentication provider (Google or Apple).",
                )
                SectionContent("You agree to:")
                SectionContent(
                    "• Provide accurate account information through the authentication provider",
                )
                SectionContent("• Use the app only for lawful purposes")
                SectionContent(
                    "• Keep your account secure and not share access with others",
                )
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "We do not manage or store user passwords.",
                )

                // Section 3
                SectionTitle("3. Acceptable Use", largeBreakpoint)
                SectionContent("You agree not to:")
                SectionContent("• Use the app for any illegal or unauthorized purpose")
                SectionContent(
                    "• Attempt to gain unauthorized access to the app or its systems",
                )
                SectionContent(
                    "• Interfere with or disrupt the app's functionality",
                )
                SectionContent(
                    "• Reverse engineer, copy, or exploit the app's services without permission",
                )

                // Section 4
                SectionTitle("4. User Data & Content", largeBreakpoint)
                SectionContent(
                    "You retain ownership of all content you create within the app, including:",
                )
                SectionContent("• Projects")
                SectionContent("• Tasks")
                SectionContent("• Time entries")
                SectionContent("• Analytics data")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "By using The Projectas, you grant us permission to store and process this data solely to provide the app's functionality.",
                )

                // Section 5
                SectionTitle("5. Notifications & Deadlines", largeBreakpoint)
                SectionContent(
                    "The Projectas may send notifications related to:",
                )
                SectionContent("• Task deadlines")
                SectionContent("• Project expiration alerts")
                SectionContent("• System or service updates")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "Notifications are provided on a best-effort basis and should not be relied upon as the sole method of deadline management.",
                )

                // Section 6
                SectionTitle("6. Service Availability", largeBreakpoint)
                SectionContent(
                    "We strive to provide a reliable service but do not guarantee uninterrupted or error-free operation.",
                )
                SectionContent("We reserve the right to:")
                SectionContent(
                    "• Modify or discontinue features at any time",
                )
                SectionContent(
                    "• Perform maintenance that may temporarily limit access",
                )

                // Section 7
                SectionTitle("7. Account Termination", largeBreakpoint)
                SectionContent(
                    "You may stop using The Projectas or delete your account at any time.",
                )
                SectionContent("We reserve the right to suspend or terminate accounts that:")
                SectionContent("• Violate these Terms")
                SectionContent("• Engage in abusive or harmful behavior")
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "Upon termination, your data will be deleted in accordance with our Privacy Policy.",
                )

                // Section 8
                SectionTitle("8. Intellectual Property", largeBreakpoint)
                SectionContent(
                    "All rights, title, and interest in The Projectas—including design, branding, logos, and software—remain the property of the app owner.",
                )
                SectionContent(
                    "You may not use our branding or assets without prior written permission.",
                )

                // Section 9
                SectionTitle("9. Limitation of Liability", largeBreakpoint)
                SectionContent(
                    "The Projectas is provided \"as is\" and \"as available.\"",
                )
                SectionContent("We are not liable for:")
                SectionContent("• Loss of data")
                SectionContent("• Missed deadlines or project delays")
                SectionContent(
                    "• Productivity or financial losses resulting from app usage",
                )
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "Use of the app is at your own risk.",
                )

                // Section 10
                SectionTitle("10. Changes to These Terms", largeBreakpoint)
                SectionContent(
                    "We may update these Terms from time to time.",
                )
                Surface(modifier = Modifier.height(20.px)) {}
                SectionContent(
                    "Continued use of the app after changes means you accept the updated Terms.",
                )

                // Section 11
                SectionTitle("11. Governing Law", largeBreakpoint)
                SectionContent(
                    "These Terms shall be governed by and interpreted in accordance with applicable laws, without regard to conflict of law principles.",
                )

                // Section 12
                SectionTitle("12. Contact Information", largeBreakpoint)
                SectionContent(
                    "For questions about these Terms, please contact us:",
                )
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(topBottom = 10.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(LATO_REGULAR)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text(SUPPORT_EMAIL)
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(30.px)) {}
    H2(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 20.px)
            .color(Theme.Text.rgb)
            .fontFamily(LATO_LIGHT)
            .fontSize(if (largeBreakpoint) 1.8.cssRem else 1.6.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SectionContent(
    text: String,
) {
    P(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.TextLowVisibility.rgb)
            .fontFamily(LATO_LIGHT)
            .fontSize(1.1.cssRem)
            .lineHeight(1.6)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}